/**
 * Menu Cart Functionality
 * Handles quantity selectors and add-to-cart without opening drawer
 */

// Only define if not already defined
if (!customElements.get('add-to-cart')) {
  // Custom element for Add to Cart button
  class AddToCart extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick(e) {
    e.preventDefault();
    
    const variantId = this.dataset.variantId;
    const deliveryType = this.dataset.deliveryType || '';
    
    if (!variantId) {
      console.error('No variant ID provided');
      return;
    }

    // Store references before async operations
    const buttonElement = this;
    const parentNode = this.parentNode;

    this.classList.add('loading');
    
    const formData = {
      items: [{
        id: variantId,
        quantity: 1
      }]
    };
    
    // Add delivery type as line item property if provided
    if (deliveryType) {
      formData.items[0].properties = {
        '_delivery_type': deliveryType
      };
    }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      console.log('Item added to cart, replacing button with quantity selector');
      
      // Replace button with quantity selector immediately
      const quantitySelector = document.createElement('quantity-selector');
      quantitySelector.setAttribute('data-variant-id', variantId);
      quantitySelector.setAttribute('data-initial-quantity', '1');
      
      if (parentNode && buttonElement.parentNode) {
        parentNode.replaceChild(quantitySelector, buttonElement);
        console.log('Button replaced successfully');
        
        // Update cart count (using a static method since buttonElement is now detached)
        AddToCart.updateGlobalCartCount();
      } else {
        console.error('Parent node not found, cannot replace button');
      }
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      buttonElement.classList.remove('loading');
      alert('There was an error adding this item to your cart. Please try again.');
    });
  }
  
  updateCartCount() {
    AddToCart.updateGlobalCartCount();
  }
  
  static updateGlobalCartCount() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update or create cart bubble counts
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        
        if (cartIconBubble) {
          let bubble = cartIconBubble.querySelector('.cart-count-bubble');
          
          if (cart.item_count > 0) {
            if (!bubble) {
              // Bubble doesn't exist (cart was empty on page load) — create it
              bubble = document.createElement('span');
              bubble.className = 'cart-count-bubble';
              bubble.innerHTML = '<span aria-hidden="true">' + cart.item_count + '</span><span class="visually-hidden">' + cart.item_count + ' items</span>';
              cartIconBubble.appendChild(bubble);
            } else {
              // Update existing bubble
              const spans = bubble.querySelectorAll('span');
              spans.forEach(span => {
                if (span.classList.contains('visually-hidden')) {
                  span.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
                } else {
                  span.textContent = cart.item_count;
                }
              });
            }
          } else if (bubble) {
            // Cart is empty — remove the bubble
            bubble.remove();
          }
        }
        
        // Update toolbar count if it exists
        const toolbarCount = document.getElementById('toolbar-item-count');
        const toolbarLabel = document.getElementById('toolbar-item-label');
        if (toolbarCount) {
          toolbarCount.textContent = cart.item_count;
        }
        if (toolbarLabel) {
          toolbarLabel.textContent = cart.item_count === 1 ? 'item selected' : 'items selected';
        }
        
        // Dispatch cart refresh to update mini-cart section + header bubble via section rendering
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { open: false }
        }));
        
        // Dispatch event for other listeners
        document.dispatchEvent(new CustomEvent('menu:cart-updated', {
          detail: { cart: cart }
        }));
      });
  }
  }

  customElements.define('add-to-cart', AddToCart);
}


// Only define if not already defined
if (!customElements.get('quantity-selector')) {
  // Custom element for Quantity Selector
  class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
    this.quantity = parseInt(this.dataset.initialQuantity) || 1;
    this.debounceTimer = null;
  }
  
  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }
  
  render() {
    this.innerHTML = `
      <div class="quantity-selector">
        <button type="button" class="quantity-selector__minus" aria-label="Decrease quantity">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 6H10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <span class="quantity-selector__value">${this.quantity}</span>
        <button type="button" class="quantity-selector__plus" aria-label="Increase quantity">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2V10M2 6H10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;
  }
  
  attachEventListeners() {
    const minusBtn = this.querySelector('.quantity-selector__minus');
    const plusBtn = this.querySelector('.quantity-selector__plus');
    
    if (minusBtn) {
      minusBtn.addEventListener('click', () => this.decrease());
    }
    
    if (plusBtn) {
      plusBtn.addEventListener('click', () => this.increase());
    }
  }
  
  increase() {
    this.quantity++;
    this.updateDisplay();
    this.updateCart();
  }
  
  decrease() {
    if (this.quantity > 0) {
      this.quantity--;
      this.updateDisplay();
      this.updateCart();
      
      // If quantity is 0, replace with add button
      if (this.quantity === 0) {
        this.replaceWithAddButton();
      }
    }
  }
  
  updateDisplay() {
    const valueElement = this.querySelector('.quantity-selector__value');
    if (valueElement) {
      valueElement.textContent = this.quantity;
    }
  }
  
  updateCart() {
    // Debounce the cart update
    clearTimeout(this.debounceTimer);
    
    this.debounceTimer = setTimeout(() => {
      this.performCartUpdate();
    }, 300);
  }
  
  performCartUpdate() {
    const updates = {};
    updates[this.variantId] = this.quantity;
    
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates })
    })
    .then(response => response.json())
    .then(cart => {
      // Use the static method to update cart count
      if (typeof AddToCart !== 'undefined' && AddToCart.updateGlobalCartCount) {
        AddToCart.updateGlobalCartCount();
      }
    })
    .catch(error => {
      console.error('Error updating cart:', error);
    });
  }
  
  replaceWithAddButton() {
    const addButton = document.createElement('add-to-cart');
    addButton.setAttribute('data-variant-id', this.variantId);
    addButton.textContent = 'ADD';
    
    this.parentNode.replaceChild(addButton, this);
  }
  }

  customElements.define('quantity-selector', QuantitySelector);
}


// Style injection for quantity selector
if (!document.getElementById('quantity-selector-styles')) {
  const style = document.createElement('style');
  style.id = 'quantity-selector-styles';
  style.textContent = `
    .quantity-selector {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 0;
      background: white;
      width: 100%;
      margin: 0 auto;
    }
    
    .quantity-selector__minus,
    .quantity-selector__plus {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333;
      transition: opacity 0.2s;
      height: 100%;
      width: 33.33%;
    }
    
    .quantity-selector__minus:hover,
    .quantity-selector__plus:hover {
      opacity: 0.6;
    }
    
    .quantity-selector__minus:disabled,
    .quantity-selector__plus:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
    
    .quantity-selector__value {
      min-width: 20px;
      width: 33.33%;
      text-align: center;
      font-weight: 600;
      font-size: 14px;
      color: #333;
    }
    
    add-to-cart {
      display: block;
      background: #000;
      color: white;
      padding: 10px 24px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      transition: background 0.2s;
      border: none;
      text-align: center;
      width: 100%;
    }
    
    add-to-cart:hover {
      background: #333;
    }
    
    add-to-cart.loading {
      opacity: 0.6;
      pointer-events: none;
    }
    
    add-to-cart.loading::after {
      content: '';
      display: inline-block;
      width: 12px;
      height: 12px;
      margin-left: 8px;
      border: 2px solid white;
      border-radius: 50%;
      border-top-color: transparent;
      animation: spinner 0.6s linear infinite;
    }
    
    @keyframes spinner {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

